import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnChanges, OnDestroy, Input, SimpleChanges, Inject, PLATFORM_ID, afterNextRender } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Menu, MenuItem } from 'src/app/shared/model/Menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UtilsService } from 'src/app/core/services/utils.service';
import { TableService } from 'src/app/core/services/table.service';
import { DiscountCouponService } from 'src/app/core/services/discount-coupon.service';

@Component({
  selector: 'app-menu-list',
  templateUrl: './menu-list.page.html',
  styleUrls: ['./menu-list.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuListPage implements OnInit, OnChanges, OnDestroy {

	@Input() menu: Menu;
	@Input() info:any;

	// Renderização progressiva: em cardápios grandes, montar todas as categorias de uma
	// vez bloqueia a main thread por vários segundos em dispositivos móveis. Renderizamos
	// as primeiras categorias imediatamente e o restante em lotes assíncronos.
	public visibleGeneralLimit = Number.MAX_SAFE_INTEGER;
	private progressiveTimer: ReturnType<typeof setTimeout> | null = null;
	// Orçamento de produtos por lote: o TBT penaliza o excedente de cada task
	// acima de 50ms — no Ionic 8 o custo por produto ~dobrou vs Ionic 5, então
	// lotes de 40 viravam tasks de 250-470ms (Lighthouse 2026-07-05). Lote de
	// 12 mantém cada task perto do limiar sem mudar o trabalho total.
	private static readonly PRODUCTS_PER_BATCH = 12;

	// View-model pré-computado (Caso 16, parte 2): isItemVisible/isItemInStock/
	// showCategory eram chamados para CADA item a CADA ciclo de CD. Como nada
	// muta visibilidade/estoque pós-render (o pipeline do restaurant.service
	// roda antes, e busca/atualização SUBSTITUEM o menu → ngOnChanges), as
	// listas visíveis são calculadas uma única vez por menu recebido.
	public featuredVisible: MenuItem[] = [];
	public combosVisible: MenuItem[] = [];
	public pizzaSizesVisible: MenuItem[] = [];
	public generalView: Array<{
		category: any;
		products: MenuItem[];
		hasDescription: boolean;
		descriptionHtml: any;
		descriptionText: string;
	}> = [];

	public pizzaCategoryName;
	public comboCategoryName;
	public featuredItemsCategoryName;
	public newHomePedirDelivery: boolean;
	// UA não muda durante a sessão: avaliar uma única vez. Guard p/ SSR (sem navigator).
	public readonly isInstagramUA = typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Instagram') > -1;

	private generalProducts: any[];

	// Caches por valor de descrição: os métodos são funções puras de string, mas eram
	// reexecutados (JSON.parse + regexes + sanitize) para cada categoria a cada change
	// detection — e getSafeHtml retornava um SafeHtml novo, re-aplicando [innerHTML].
	private safeHtmlCache = new Map<string, any>();
	private visibleDescriptionCache = new Map<string, boolean>();
	private plainTextCache = new Map<string, string>();

	constructor(
		private translatePipe: TranslatePipe,
		private utils: UtilsService,
		private tableService: TableService,
		private discountCouponService: DiscountCouponService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: Object,
	) {
		// Render adiado do menu (Fase 2 perf): o server e a 1ª CD do cliente saem
		// VAZIOS (menos ion-cards hidratados = menos TBT). Após a hidratação, o
		// cliente preenche (rebuild + render progressivo). afterNextRender NÃO roda
		// no server → o render prerenderizado do menu-list fica vazio (o header/LCP,
		// que é outro componente, continua prerenderizado normalmente).
		afterNextRender(() => {
			// startProgressiveRender já constrói os VMs incrementalmente (1º lote só).
			this.startProgressiveRender();
			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		this.generalProducts = this.menu.general;
		this.pizzaCategoryName = this.info.pizza_category_name.length > 0 ? this.info.pizza_category_name : 'Pizzas';
		this.comboCategoryName = this.info.combo_category_name.length > 0 ? this.info.combo_category_name : 'Combos';
		this.featuredItemsCategoryName = this.translatePipe.transform('On Sale');
		this.newHomePedirDelivery = !!this.info.modules.new_home_pedir_delivery;
		// Hero (destaques/combos/pizzas) + o 1º lote PEQUENO da lista geral renderizam no
		// server + 1ª CD do cliente: cards reais no HTML estático ANCORAM o LCP cedo (no
		// FCP). O resto (o grosso dos cards) fica diferido e o afterNextRender preenche no
		// browser após hidratar → TBT baixo. Hidratação casa (server e cliente = mesmo lote).
		this.rebuildHero();
		this.generalView = [];
		this.buildGeneralUpTo(this.categoryCountForBudget(this.menu?.general || [], 0));
		this.announceCouponFromLink();
	}

	/**
	 * Chegada pelo link compartilhável /c/<código> (Cupom v2): confirma pro cliente que o
	 * cupom foi guardado e será aplicado no pagamento. Browser-only: o ngOnInit também
	 * roda no prerender do server, e toast é UI imperativa. O anúncio é consumível UMA
	 * vez, então revisitas ao cardápio (com o cupom ainda pendente) não repetem o toast.
	 * Flag new_coupon OFF: nada é consumido nem mostrado, mesma degradação do restante.
	 * Modo MESA idem: o pedido de mesa sai direto do carrinho/entrega, sem passar pela
	 * tela de pagamento, então "será aplicado no pagamento" seria promessa falsa.
	 */
	private announceCouponFromLink() {
		if (!isPlatformBrowser(this.platformId) || !this.discountCouponService.isActive || this.tableService.currentTable) return;

		const code = this.discountCouponService.consumeLinkAnnouncement();
		if (!code) return;

		this.utils.createToaster(`Cupom ${code} ativado! Monte seu pedido que ele será aplicado no pagamento.`, 5000, 'success');
	}

	ngOnChanges(changes: SimpleChanges) {
		// menu substituído após o init (ex.: resultados de busca no layout legado):
		// renderiza tudo de uma vez — resultados são pequenos e o usuário espera vê-los completos
		if (changes.menu && !changes.menu.firstChange) {
			this.rebuildViewModel();
			this.renderAllNow();
		}
	}

	/** Recalcula as listas visíveis — uma vez por menu, nunca por ciclo de CD. */
	private rebuildViewModel() {
		this.rebuildHero();
		this.rebuildGeneral();
	}

	/** Hero above-the-fold (destaques/combos/pizzas): leve, renderiza no server (ancora o LCP). */
	private rebuildHero() {
		const visivel = (item: any) => this.isItemVisible(item) && this.isItemInStock(item);
		this.featuredVisible = (this.menu?.featuredItems || []).filter(visivel);
		this.combosVisible = (this.menu?.combos || []).filter(visivel);
		this.pizzaSizesVisible = (this.menu?.pizzas?.sizes || []).filter(visivel);
	}

	/** Lista geral: reconstrói TUDO (usado pela busca/renderAllNow). */
	private rebuildGeneral() {
		this.generalView = [];
		this.buildGeneralUpTo(Number.MAX_SAFE_INTEGER);
	}

	/** View-model de UMA categoria (filtra produtos + sanitiza descrição). */
	private buildCategoryVM(category: any) {
		const visivel = (item: any) => this.isItemVisible(item) && this.isItemInStock(item);
		const hasDescription = this.hasVisibleDescription(category.description);
		return {
			category,
			products: (category.unavailable || category.unavailableByTemplate) ? [] :
				(category.products || []).filter(visivel),
			hasDescription,
			descriptionHtml: hasDescription ? this.getSafeHtml(category.description) : null,
			descriptionText: hasDescription ? this.removeHtmlTags(category.description) : '',
		};
	}

	/** Constrói os VMs de categoria INCREMENTALMENTE até `limit` (não reprocessa os já feitos).
	 *  Chave p/ o LCP: o 1º paint do menu não paga o processamento das 65 categorias de uma vez. */
	private buildGeneralUpTo(limit: number) {
		const source = this.menu?.general || [];
		const target = Math.min(limit, source.length);
		for (let i = this.generalView.length; i < target; i++) {
			this.generalView.push(this.buildCategoryVM(source[i]));
		}
		this.visibleGeneralLimit = this.generalView.length;
	}

	trackByCategory(index: number, view: any): any {
		return view?.category?.id ?? index;
	}

	ngOnDestroy() {
		this.cancelProgressiveRender();
	}

	private startProgressiveRender() {
		const source = this.menu?.general || [];
		// NÃO reseta: o 1º lote já foi construído no ngOnInit (server + cliente, p/ casar a
		// hidratação e ancorar o LCP). Aqui só agenda a construção incremental do RESTANTE.
		if (this.visibleGeneralLimit >= source.length) return;
		if (!isPlatformBrowser(this.platformId)) return;
		const step = () => {
			this.progressiveTimer = null;
			// constrói o PRÓXIMO lote de VMs (incremental) e o exibe
			const next = this.visibleGeneralLimit + this.categoryCountForBudget(source, this.visibleGeneralLimit);
			this.buildGeneralUpTo(next);
			// OnPush: mutação fora de input/evento — marca p/ renderizar no próximo tick.
			this.cdr.markForCheck();
			if (this.visibleGeneralLimit < source.length) {
				this.progressiveTimer = setTimeout(step, 48);
			}
		};
		this.progressiveTimer = setTimeout(step, 48);
	}

	// quantas categorias (>= 1) cabem no orçamento de produtos por lote, a partir
	// de um índice — conta só os produtos VISÍVEIS (view-model já filtrado)
	private categoryCountForBudget(categories: Array<{ products: any[] }>, startIndex: number): number {
		let count = 0, products = 0;
		for (let i = startIndex; i < categories.length; i++) {
			products += categories[i].products.length;
			count++;
			if (products >= MenuListPage.PRODUCTS_PER_BATCH) break;
		}
		return Math.max(count, 1);
	}

	// trackBy (Caso 16): sem ele, trocar a referência do menu (ex.: busca no
	// layout legado) recriava o DOM de TODOS os cards em vez de reaproveitar.
	trackById(index: number, item: any): any {
		return item?.id ?? index;
	}

	private cancelProgressiveRender() {
		if (this.progressiveTimer !== null) {
			clearTimeout(this.progressiveTimer);
			this.progressiveTimer = null;
		}
	}

	/** Monta imediatamente todas as categorias (necessário para âncoras/scroll-restore). */
	renderAllNow() {
		this.cancelProgressiveRender();
		this.buildGeneralUpTo(Number.MAX_SAFE_INTEGER);
		this.cdr.markForCheck();
	}

	get isFullyRendered(): boolean {
		// generalView agora contém só as categorias JÁ construídas (render incremental);
		// "totalmente renderizado" = construímos todas as categorias da fonte.
		return this.generalView.length >= (this.menu?.general?.length || 0);
	}

	get showStockPaused(): boolean {
		if (this.info?.modules?.ingredient_inventory) {
			return this.info?.supply_stock_config?.display_paused_items_as_sold_out ?? false;
		}
		return this.info?.stock_configuration?.display_paused_items_as_sold_out ?? false;
	}

	isItemVisible(item: MenuItem): boolean {
		return !item.unavailable && !item.unavailableByTemplate && item.totalCalcType !== 'WEIGHT';
	}

	isItemInStock(item: MenuItem): boolean {
		return item.hasStock !== false || this.showStockPaused;
	}

	showCategory(items: MenuItem[]): boolean {
		if (!items) return false;
		return items.some(item => this.isItemVisible(item) && this.isItemInStock(item));
	}

	logScrolling(evento) {
		console.log(evento);
	}

	convertToSafeHtml(rawHtml: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    }

	isInstagram() {
		var ua = navigator.userAgent;
		return (ua.indexOf('Instagram') > -1) ? true : false;
	}

	removeHtmlTags(str: string) {
		const cached = this.plainTextCache.get(str);
		if (cached !== undefined) return cached;
		const result = this.utils.removeTags(str);
		this.plainTextCache.set(str, result);
		return result;
	}

	hasVisibleDescription(desc: string): boolean {
		if (!desc) return false;
		const cached = this.visibleDescriptionCache.get(desc);
		if (cached !== undefined) return cached;
		const result = !!this.removeHtmlTags(desc)?.trim();
		this.visibleDescriptionCache.set(desc, result);
		return result;
	}

    getSafeHtml(value: any) {
		const cached = this.safeHtmlCache.get(value);
		if (cached !== undefined) return cached;
		let result;
		try {
			const parsedValue = JSON.parse(value);
			const sanitizedValue = this.utils.sanitizeAndFormatText(parsedValue);
			result = this.convertToSafeHtml(sanitizedValue);
		} catch (error) {
			const sanitizedValue = this.utils.sanitizeAndFormatText(value);
			result = this.removeHtmlTags(sanitizedValue);
		}
		this.safeHtmlCache.set(value, result);
		return result;
    }
}
